import { previewRequest } from 'link-previewer/client';
import { NOTIFICATION_MSG_RECEIVED } from '../../../constants/language';
import {
  RECEIVE_MESSAGE,
  SEND_MESSAGE,
  ADD_PREVIEW_MESSAGE,
  TYPINGS_CHANGED,
  RECEIVE_CANNED_RESPONSE,
  SUBMIT_INPUT_AREA,
  MESSAGE_CHANGE,
  SEND_EDITED_MESSAGE,
  updateRoomsList,
  receiveMessage,
  acceptCall,
  addPreviewMessage,
  sendMessage,
  sendEditedMessage,
  showBrowserNotification,
  updateRoomTypings,
  updateCannedResponseTitles,
} from '../../actions'
import { updateItemByPropertyName } from '../../../utils/immutable/objectArray'
import NetJsService from '../../../services/net/NetJsService'
import {
  createMessage, addMessage, hasLinkPreview, regLink,
} from '../../../../../../../module/dev/room/message-display-area/messages/messageHelpers'
import parseCannedResponse from '../../../utils/canned-response/cannedResponse'
import { transformUnicode10to16 } from '../../../../../../../module/dev/libs/common/transformUnicode10to16'
import { RoomStatuses } from '../../constants'
import findLinkOrEmail from '../../../../../../../module/dev/room/message-display-area/messages/utils/findLinkOrEmail'
import { MessageSubtypes } from '../../../../../../../module/dev/room/message-display-area/messages/constants'
import isLinkToFile from '../../../../../../../module/dev/libs/common/isLinkToFile'
import runOnceAtIntervalTime from '../../../../../../../module/dev/libs/utils/runOnceAtIntervalTime'

const BLOCK_NOTIFICATION_TIMEOUT = 12000
export const textDisplayTemplate = ({ originalTextHtml, translatedTextHtml }) => {
  if (originalTextHtml && translatedTextHtml) {
    return `${originalTextHtml}<span class="line"></span>${translatedTextHtml}`
  }
  return translatedTextHtml || originalTextHtml
}

/* If messages arrive too frequently, they are queued. */
let messagesInAddQueue = []
const CREATE_MESSAGE_INTERVAL = 300
const updateRoomsOnceAtIntervalTime = runOnceAtIntervalTime({
  interval: CREATE_MESSAGE_INTERVAL,
  callback: ({ dispatch, getState }) => {
    let { rooms: { rooms, currentRoom } } = getState()
    let newRooms = rooms
    messagesInAddQueue.forEach(({ message, roomId, isLinkPreview }) => {
      const room = newRooms.find(r => r.id === roomId);
      if (!room) {
        console.warn('The room is missing!', message, roomId, JSON.parse(JSON.stringify(newRooms)), JSON.parse(JSON.stringify(rooms)))
        return
      }
      const newMessages = addMessage({ messages: room.messages, newMessage: message, roomId });
      const newRoomData = { messages: newMessages };
      if (room.visited && roomId !== currentRoom && !isLinkPreview) {
        newRoomData.unreadMessages = room.unreadMessages + 1;
      }
      newRooms = updateItemByPropertyName(newRooms, roomId, { ...newRoomData })
    })
    dispatch(updateRoomsList(newRooms))
    messagesInAddQueue = []
  },
})
export const processReceiveMessage = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === RECEIVE_MESSAGE) {
    const { payload, meta: { roomId } } = action;
    let {
      rooms: { rooms },
      settings: { linkPreviewEnabled, notificationEnabled },
      texts: { texts },
      app: { lastTimeLogin },
    } = getState()
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    const { subtype, text, isFake, nickname, type } = payload;
    const isLinkPreview = subtype === MessageSubtypes.PREVIEW;
    const message = createMessage({
      ...payload, visitorServerUrl: window.visitorServerUrl, textDisplayTemplate,
    });

    messagesInAddQueue.push({ message, roomId, isLinkPreview })
    updateRoomsOnceAtIntervalTime({ dispatch, getState })

    const links = findLinkOrEmail(text, regLink).links;
    if (links.length && linkPreviewEnabled && !isFake && type !== 'system') {
      dispatch(addPreviewMessage(links, message, roomId));
    }

    const isConsoleOnFocus = document.hasFocus();
    if (lastTimeLogin
      && (Date.now() - lastTimeLogin > BLOCK_NOTIFICATION_TIMEOUT)
      && !isConsoleOnFocus
      && notificationEnabled
      && Notification.permission === 'granted'
      && type === 'visitor' && !isLinkPreview
    ) {
      const title = `${texts[NOTIFICATION_MSG_RECEIVED]} ${nickname}`;
      const body = transformUnicode10to16(text);

      dispatch(showBrowserNotification(title, body, room.id));
    }
  }
}

export const processAddPreviewMessage = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === ADD_PREVIEW_MESSAGE) {
    const { payload, meta } = action;
    const { message: { clientTimestamp, type, nickname }, links } = payload;
    const { rooms } = getState().rooms;

    const messages = rooms.find(r => r.id === meta.roomId).messages;
    for (const link of links) {
      const newClientTimestamp = (parseInt(clientTimestamp, 10) + 1).toString();
      if (hasLinkPreview(messages, clientTimestamp, link)) continue;
      const isFile = isLinkToFile(link)
      if (isFile) continue

      const onPreviewDataReceived = previewData => {
        const { title, description, url, images } = previewData;
        const date = `${Date.now()}`;
        const image = images[0] || null;
        const domain = url.split('/')[2];

        dispatch(receiveMessage(
          meta.roomId,
          {
            id: date,
            title,
            description,
            image,
            url: link,
            domain,
            text: '',
            nickname,
            parentClientTimestamp: clientTimestamp,
            clientTimestamp: newClientTimestamp,
            date,
            type,
            subtype: MessageSubtypes.PREVIEW,
          },
          false,
        ));
      }

      previewRequest(window.lpvDomain, link, onPreviewDataReceived);
    }
  }
}

export const processSendMessage = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === SEND_MESSAGE) {
    const { meta: { roomId }, payload } = action;
    const { session: { nickname }, rooms: { rooms } } = getState();
    const clientTimestamp = `${Date.now()}`;
    const currentRoom = rooms.find(r => r.id === roomId);

    NetJsService.sendMessage(roomId, clientTimestamp, payload);

    if (currentRoom && currentRoom.status === RoomStatuses.VISITOR_CALLING) {
      dispatch(acceptCall(roomId));
    }

    const messageParams = {
      nickname,
      text: payload,
      type: 'operator',
      subtype: 'message',
      clientTimestamp,
      isFake: true,
    };

    dispatch(receiveMessage(roomId, messageParams, true));
  }
};

export const processReceiveCannedResponseTitles = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === RECEIVE_CANNED_RESPONSE) {
    const { payload } = action;

    const unparsedTitles = payload;
    const cannedResponseTitles = parseCannedResponse(payload);

    dispatch(updateCannedResponseTitles(cannedResponseTitles, unparsedTitles));
  }
};

export const processTypingsChanged = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === TYPINGS_CHANGED) {
    const { payload, meta } = action;

    dispatch(updateRoomTypings(meta.roomId, payload));
  }
}

export const processMessageChange = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === MESSAGE_CHANGE) {
    const { rooms, currentRoom: currentRoomId } = getState().rooms
    const currentRoom = rooms.find(room => room.id === currentRoomId)
    NetJsService.changeTyping(currentRoomId)
    if (currentRoom.status === RoomStatuses.VISITOR_CALLING) {
      dispatch(acceptCall(currentRoomId))
    }
  }
}

export const processSubmitInputArea = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === SUBMIT_INPUT_AREA) {
    const { rooms, currentRoom: currentRoomId } = getState().rooms
    const currentRoom = rooms.find(room => room.id === currentRoomId)
    const isEditableMessage = currentRoom.editableMessage.id
    if (isEditableMessage) {
      const messageText = currentRoom.editableMessage.text.trim()
      if (!messageText) return
      dispatch(sendEditedMessage({
        roomId: currentRoomId, messageId: currentRoom.editableMessage.id, text: messageText,
      }))
    } else {
      const messageText = currentRoom.draftMessage.trim()
      if (!messageText) return
      dispatch(sendMessage(currentRoomId, messageText))
    }
  }
}

export const processSendEditedMessage = ({ dispatch, getState }) => next => action => {
  next(action);

  if (action.type === SEND_EDITED_MESSAGE) {
    NetJsService.editMessage(action.payload)
  }
}

export default [
  processSendMessage,
  processTypingsChanged,
  processReceiveMessage,
  processAddPreviewMessage,
  processReceiveCannedResponseTitles,
  processMessageChange,
  processSubmitInputArea,
  processSendEditedMessage,
];
